import { useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useTableQueryBuilder } from "@/hooks";
import { useIntegrations } from "@/hooks/use-integrations";
import type { OutputItem } from "@/types/post-purchase.ts";
import { SinglePageTable } from "@/ui";
import { compareSemver } from "@/utils/normalize-semver";
import { AddModelVersionModal, DeployModelModal, ModelDescriptionCell } from "./-components";

type ClientFamilyVote = {
  family: string;
  highestVersion: string;
  votes: number;
};

const columns: ColumnDef<OutputItem>[] = [
  {
    id: "model",
    accessorKey: "name",
    header: "Name",
    enableSorting: false,
  },
  {
    id: "clientModelFamily",
    accessorKey: "clientModelFamily",
    header: "Model Family",
    enableSorting: false,
    cell: ({ row }) => {
      return row.original.clientModelFamily ?? "—";
    },
  },
  {
    id: "currentVersion",
    accessorKey: "currentVersion",
    header: "Current Version",
    enableSorting: false,
  },
  {
    id: "clientVersion",
    accessorKey: "clientVersion",
    header: "Client Version",
    enableSorting: false,
    cell: ({ row }) => {
      return row.original.clientVersion ?? "—";
    },
  },
  {
    id: "description",
    accessorKey: "description",
    header: "Description",
    enableSorting: false,
    cell: ({ row }) => {
      const description = row.original.description;
      if (!description) {
        return null;
      }

      return <ModelDescriptionCell description={description} />;
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-end gap-4">
          <DeployModelModal model={row.original} />
          <AddModelVersionModal model={row.original} />
        </div>
      );
    },
  },
] as const;

const BayesiaModelsPage = () => {
  const { queryBuilder, setSorting, sortingState } = useTableQueryBuilder({ filterKeys: [] });

  const { data: bayesiaModels, isFetching } = useIntegrations().getBayesiaModels({ queryBuilder });

  const dataTransformed = useCallback(() => {
    if (!bayesiaModels) {
      return [];
    }

    const clientFamilyVotes = new Map<string, Map<string, ClientFamilyVote>>();

    const map = bayesiaModels.data.reduce((acc, item) => {
      if (!acc.has(item.name)) {
        acc.set(item.name, {
          id: item.id,
          name: item.name,
          versions: [],
          currentVersion: null,
          description: item.description,
          clientModelFamily: null,
          clientVersion: null,
          createdAt: item.createdAt,
        });
      }

      const entry = acc.get(item.name)!;
      entry.versions.push({
        number: item.version,
        description: item.description,
        createdAt: item.createdAt,
        clientVersion: item.clientVersion,
      });

      if (item.clientModelFamily) {
        const votesByFamily =
          clientFamilyVotes.get(item.name) ?? new Map<string, ClientFamilyVote>();
        const familyKey = item.clientModelFamily.toLowerCase();
        const vote = votesByFamily.get(familyKey);

        votesByFamily.set(familyKey, {
          family: vote?.family ?? item.clientModelFamily,
          highestVersion:
            vote === undefined || compareSemver(vote.highestVersion, item.version) > 0
              ? item.version
              : vote.highestVersion,
          votes: (vote?.votes ?? 0) + 1,
        });

        clientFamilyVotes.set(item.name, votesByFamily);
      }

      if (item.isActive) {
        entry.currentVersion = item.version;
        entry.description = item.description;
        entry.clientVersion = item.clientVersion;
      }

      return acc;
    }, new Map<string, OutputItem>());

    return Array.from(map.values()).map((entry) => {
      const anchor = Array.from(
        clientFamilyVotes.get(entry.name)?.values() ?? [],
      ).reduce<ClientFamilyVote | null>((winner, candidate) => {
        if (winner === null || candidate.votes > winner.votes) {
          return candidate;
        }

        if (
          candidate.votes === winner.votes &&
          compareSemver(winner.highestVersion, candidate.highestVersion) > 0
        ) {
          return candidate;
        }

        return winner;
      }, null);

      return {
        ...entry,
        clientModelFamily: anchor?.family ?? null,
        versions: entry.versions.sort((a, b) => {
          return compareSemver(a.number, b.number);
        }),
      };
    });
  }, [bayesiaModels]);

  const transformedModels = dataTransformed();

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <SinglePageTable
        columns={columns}
        data={transformedModels}
        isLoadingData={isFetching}
        rowsPerPage={50}
        setSorting={setSorting}
        sorting={sortingState}
      />
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/integrations/bayesia/models/")({
  component: BayesiaModelsPage,
});
